import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  formatIsoDate,
  formatLongDate,
  MONTH_LABELS,
  startOfDay,
  WEEKDAY_LABELS,
  WEEKDAY_NAMES,
  weeksOf,
} from "@/lib/calendarDates";

export type DayVariant = "selected" | "inRange" | "none";

export function CalendarGrid({
  activeDate,
  onActiveDateChange,
  onSelect,
  variantOf,
  minDate,
  maxDate,
  label,
}: {
  activeDate: Date;
  onActiveDateChange: (date: Date) => void;
  onSelect: (date: Date) => void;
  variantOf: (iso: string) => DayVariant;
  minDate?: Date;
  maxDate?: Date;
  label: string;
}) {
  const dayRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const shouldFocusActive = useRef(false);

  useEffect(() => {
    if (!shouldFocusActive.current) {
      return;
    }

    shouldFocusActive.current = false;
    dayRefs.current.get(formatIsoDate(activeDate))?.focus();
  });

  const viewYear = activeDate.getFullYear();
  const viewMonth = activeDate.getMonth();
  const weeks = weeksOf(viewYear, viewMonth);
  const today = startOfDay(new Date());
  const activeIso = formatIsoDate(activeDate);

  function isDisabled(day: Date) {
    return Boolean((minDate && day < minDate) || (maxDate && day > maxDate));
  }

  function clamp(date: Date) {
    const day = startOfDay(date);

    if (minDate && day < minDate) {
      return minDate;
    }

    if (maxDate && day > maxDate) {
      return maxDate;
    }

    return day;
  }

  function moveActive(next: Date, viaKeyboard: boolean) {
    shouldFocusActive.current = viaKeyboard;
    onActiveDateChange(clamp(next));
  }

  function moveActiveByDays(days: number) {
    const next = new Date(activeDate);
    next.setDate(next.getDate() + days);
    moveActive(next, true);
  }

  function moveActiveByMonths(months: number, viaKeyboard: boolean) {
    const target = new Date(viewYear, viewMonth + months, 1);
    const lastDay = new Date(
      target.getFullYear(),
      target.getMonth() + 1,
      0,
    ).getDate();

    moveActive(
      new Date(
        target.getFullYear(),
        target.getMonth(),
        Math.min(activeDate.getDate(), lastDay),
      ),
      viaKeyboard,
    );
  }

  function handleGridKeyDown(e: React.KeyboardEvent) {
    const moves: Record<string, () => void> = {
      ArrowLeft: () => moveActiveByDays(-1),
      ArrowRight: () => moveActiveByDays(1),
      ArrowUp: () => moveActiveByDays(-7),
      ArrowDown: () => moveActiveByDays(7),
      Home: () => moveActiveByDays(-((activeDate.getDay() + 6) % 7)),
      End: () => moveActiveByDays(6 - ((activeDate.getDay() + 6) % 7)),
      PageUp: () => moveActiveByMonths(-1, true),
      PageDown: () => moveActiveByMonths(1, true),
    };

    const move = moves[e.key];

    if (!move) {
      return;
    }

    e.preventDefault();
    move();
  }

  const previousDisabled = Boolean(
    minDate && new Date(viewYear, viewMonth, 0) < minDate,
  );
  const nextDisabled = Boolean(
    maxDate && new Date(viewYear, viewMonth + 1, 1) > maxDate,
  );

  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => moveActiveByMonths(-1, false)}
          disabled={previousDisabled}
          aria-label="Previous month"
          className="rounded-lg p-1.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2} />
        </button>
        <span className="text-sm text-foreground">
          {MONTH_LABELS[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={() => moveActiveByMonths(1, false)}
          disabled={nextDisabled}
          aria-label="Next month"
          className="rounded-lg p-1.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
      <div
        role="grid"
        aria-label={label}
        onKeyDown={handleGridKeyDown}
        className="grid grid-cols-7 gap-1 text-center"
      >
        <div role="row" className="contents">
          {WEEKDAY_LABELS.map((weekday, index) => (
            <span
              key={weekday}
              role="columnheader"
              aria-label={WEEKDAY_NAMES[index]}
              className="py-1 text-xs text-foreground/70"
            >
              {weekday}
            </span>
          ))}
        </div>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} role="row" className="contents">
            {week.map((day, dayIndex) => {
              if (!day) {
                return (
                  <span
                    key={`blank-${weekIndex}-${dayIndex}`}
                    role="gridcell"
                  />
                );
              }

              const iso = formatIsoDate(day);
              const variant = variantOf(iso);
              const disabled = isDisabled(day);
              const isToday = iso === formatIsoDate(today);

              return (
                <span
                  key={day.getDate()}
                  role="gridcell"
                  aria-selected={variant !== "none"}
                >
                  <button
                    ref={(element) => {
                      dayRefs.current.set(iso, element);
                    }}
                    type="button"
                    disabled={disabled}
                    tabIndex={iso === activeIso ? 0 : -1}
                    aria-label={formatLongDate(day)}
                    aria-current={isToday ? "date" : undefined}
                    onClick={() => {
                      moveActive(day, false);
                      onSelect(day);
                    }}
                    className={`w-full rounded-lg py-1 text-sm disabled:cursor-not-allowed disabled:text-foreground/50 ${
                      variant === "selected"
                        ? "bg-accent text-surface"
                        : variant === "inRange"
                          ? "bg-muted text-foreground"
                          : disabled
                            ? ""
                            : `text-foreground hover:bg-muted ${isToday ? "border border-accent" : ""}`
                    }`}
                  >
                    {day.getDate()}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}
